import {
  Box,
  Grid,
  Stack,
  darken,
  lighten,
  Collapse,
  Checkbox,
  useTheme,
  Typography,
} from "@mui/material";
import React from "react";
import { useFormContext } from "react-hook-form";
import {
  InputLabel,
  HookFormTextField,
} from "@/shared/common/formHelper/form_components";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { emailRegEx } from "@/shared/common/formHelper/form_validators";
import { PhoneNumberInputV2 } from "@/shared/base/components/FormComponents/PhoneNumberInputV2";
import { PhoneNumberInput } from "@/shared/base/components/FormComponents/PhoneNumberInput";
/*
 +------------------------------------------------------------------------------+
 * [ main export, following compounding pattern ]
 +------------------------------------------------------------------------------+ */
export const AdditionalDetails = {
  BaseForm: BaseForm,
  DisclaimerForm: DisclaimerForm,
};
// base form component
function BaseForm(props: { onSubmit?: (values: any) => void }) {
  const { watch, register } = useFormContext();
  const isCateringSame = watch("isCateringSame");
  // default required message
  const requiredMessage = "This field is required";
  const theme = useTheme();
  const { languages, uiConfigs } = useTeamConfig();
  const cateringDisclaimer = languages?.purchaseSeats?.cateringDisclaimer || null; // prettier-ignore
  const hideCateringAndTicketContact = uiConfigs?.checkoutPage?.checkoutForm?.hideCateringAndTicketContact // prettier-ignore
  const optionalAdditionalDetailsContractTitle = uiConfigs?.checkoutPage?.checkoutForm?.optionalAdditionalDetailsContractTitle // prettier-ignore
  const optionalAdditionalDetailsContractEmail = uiConfigs?.checkoutPage?.checkoutForm?.optionalAdditionalDetailsContractEmail // prettier-ignore
  const optionalAdditionalDetailsCompanyName = uiConfigs?.checkoutPage?.checkoutForm?.optionalAdditionalDetailsCompanyName // prettier-ignore
  const optionalAdditionalDetailsContractFirstName = uiConfigs?.checkoutPage?.checkoutForm?.optionalAdditionalDetailsContractFirstName // prettier-ignore
  const optionalAdditionalDetailsContractLastName = uiConfigs?.checkoutPage?.checkoutForm?.optionalAdditionalDetailsContractLastName // prettier-ignore
  const optionalAdditionalDetailsContractPhoneNumber = uiConfigs?.checkoutPage?.checkoutForm?.optionalAdditionalDetailsContractPhoneNumber // prettier-ignore
  const optionalAdditionalDetailsCateringName = uiConfigs?.checkoutPage?.checkoutForm?.optionalAdditionalDetailsCateringName // prettier-ignore
  const optionalAdditionalDetailsCateringPhone = uiConfigs?.checkoutPage?.checkoutForm?.optionalAdditionalDetailsCateringPhone // prettier-ignore
  const optionalAdditionalDetailsCateringEmail = uiConfigs?.checkoutPage?.checkoutForm?.optionalAdditionalDetailsCateringEmail // prettier-ignore

  const hideContractFirstName = uiConfigs?.checkoutPage?.checkoutForm?.hideContractFirstName // prettier-ignore
  const hideContractLastName = uiConfigs?.checkoutPage?.checkoutForm?.hideContractLastName // prettier-ignore
  const hideContractTitle = uiConfigs?.checkoutPage?.checkoutForm?.hideContractTitle // prettier-ignore
  const hideContractAddress = uiConfigs?.checkoutPage?.checkoutForm?.hideContractAddress ?? true // prettier-ignore

  const sameAsContractSignerDefaultChecked = uiConfigs?.checkoutPage?.checkoutForm?.sameAsContractSignerDefaultChecked // prettier-ignore

  const sameAsContractSignerLabel = languages?.purchaseSeats?.sameAsContractSignerLabel || "Same as Contract Signer"; // prettier-ignore

  return (
    <Box>
      <Box>
        <Typography fontSize={"20px"} fontWeight={700}>
          Additional Details
        </Typography>
      </Box>
      <Box mt={1}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <HookFormTextField
              name="companyName"
              placeholder="Company Name"
              options={
                optionalAdditionalDetailsCompanyName
                  ? {}
                  : { required: requiredMessage }
              }
            />
          </Grid>
          {!hideContractFirstName ? (
            <Grid item xs={12}>
              <HookFormTextField
                name="contractFirstName"
                placeholder="First Name of Contact (Contract Signer)"
                options={
                  optionalAdditionalDetailsContractFirstName
                    ? {}
                    : { required: requiredMessage }
                }
              />
            </Grid>
          ) : null}
          {!hideContractLastName ? (
            <Grid item xs={12}>
              <HookFormTextField
                name="contractLastName"
                placeholder="Last Name of Contact (Contract Signer)"
                options={
                  optionalAdditionalDetailsContractLastName
                    ? {}
                    : { required: requiredMessage }
                }
              />
            </Grid>
          ) : null}
          {!hideContractTitle ? (
            <Grid item xs={12}>
              <HookFormTextField
                name="contractTitle"
                placeholder="Title"
                options={
                  optionalAdditionalDetailsContractTitle
                    ? {}
                    : { required: requiredMessage }
                }
              />
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <HookFormTextField
              type="email"
              name="contractEmail"
              placeholder="Email"
              options={{
                required: optionalAdditionalDetailsContractEmail
                  ? false
                  : requiredMessage,
                pattern: {
                  value: emailRegEx,
                  message: "Invalid Email",
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <PhoneNumberInputV2
              fieldName="contractPhoneNumber"
              options={{
                required: optionalAdditionalDetailsContractPhoneNumber
                  ? false
                  : "Phone Number is required",
              }}
            />
          </Grid>
          {!hideContractAddress ? (
            <Grid item xs={12}>
              <HookFormTextField name="contractAddress" placeholder="Address" />
            </Grid>
          ) : null}
        </Grid>

        {uiConfigs?.checkoutPage?.checkoutForm?.specialOccasionInputField ? (
          <Box mt={2}>
            <HookFormTextField
              name="specialOccasion"
              placeholder="Is there a special occasion for your visit?"
            />
          </Box>
        ) : null}

        {!hideCateringAndTicketContact ? (
          <>
            <Box mt={2}>
              <Typography fontSize={"1rem"} fontWeight={600}>
                Catering and Ticket Contact
              </Typography>
            </Box>
            {cateringDisclaimer ? (
              <Box
                sx={{
                  mt: 1,
                  backgroundColor: lighten(theme.palette.warning.main, 0.95),
                  p: 1,
                  borderRadius: 0.8,
                  color: darken(theme.palette.warning.main, 0.5),
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    lineHeight: "1.1 !important",
                    fontSize: "0.8rem !important",
                  }}
                >
                  {cateringDisclaimer}
                </Typography>
              </Box>
            ) : null}
            <Box mt={1}>
              <Stack direction={"row"} alignItems={"center"}>
                <Box>
                  <Checkbox
                    {...register("isCateringSame")}
                    sx={{ marginLeft: "-8px" }}
                    defaultChecked={sameAsContractSignerDefaultChecked}
                  />
                </Box>
                <Box>
                  <InputLabel>{sameAsContractSignerLabel}</InputLabel>
                </Box>
              </Stack>
            </Box>
            <Collapse
              defaultChecked={sameAsContractSignerDefaultChecked}
              in={!isCateringSame}
              unmountOnExit={true}
            >
              <Box mt={1}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <HookFormTextField
                      name="cateringName"
                      placeholder="Full Name"
                      options={{
                        required:
                          !isCateringSame &&
                          !optionalAdditionalDetailsCateringName
                            ? requiredMessage
                            : false,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <PhoneNumberInput
                      fieldName="cateringPhone"
                      options={{
                        required:
                          !isCateringSame &&
                          !optionalAdditionalDetailsCateringPhone
                            ? requiredMessage
                            : false,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <HookFormTextField
                      name="cateringEmail"
                      placeholder="Email"
                      options={{
                        required:
                          !isCateringSame &&
                          !optionalAdditionalDetailsCateringEmail
                            ? requiredMessage
                            : false,
                        pattern: {
                          value: emailRegEx,
                          message: "Invalid Email",
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </>
        ) : null}
      </Box>
    </Box>
  );
}
function DisclaimerForm() {
  const theme = useTheme();
  const { languages, uiConfigs, purchaseSeatConfigs } = useTeamConfig();
  const optionalAdditionalDetailsDisclaimerFullName = uiConfigs?.checkoutPage?.checkoutForm?.optionalAdditionalDetailsDisclaimerFullName; // prettier-ignore
  const disclaimerFullNamePlaceholder = purchaseSeatConfigs?.checkout?.additionalDetails?.disclaimerFullNamePlaceholder || "Full Name"; // prettier-ignore

  return (
    <>
      {uiConfigs?.checkoutPage?.checkoutForm?.additionalInputFields ? (
        <Box mb={2}>
          {uiConfigs?.checkoutPage?.checkoutForm?.additionalInputFields}
        </Box>
      ) : null}
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <HookFormTextField
            name="disclaimerFullName"
            placeholder={disclaimerFullNamePlaceholder}
            options={
              optionalAdditionalDetailsDisclaimerFullName
                ? {}
                : { required: "This field is required" }
            }
          />
        </Grid>
        {languages?.additionalDetailsDisclaimerTC ? (
          <Grid item xs={12}>
            <Box
              sx={{
                backgroundColor: lighten(theme.palette.warning.main, 0.95),
                p: 1,
                borderRadius: 0.8,
                color: darken(theme.palette.warning.main, 0.5),
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  lineHeight: "1.1 !important",
                  fontSize: "0.8rem !important",
                }}
              >
                {languages?.additionalDetailsDisclaimerTC}
              </Typography>
            </Box>
          </Grid>
        ) : null}
      </Grid>
    </>
  );
}
